export class ObjectHandler {
  constructor(canvas, bounds, objects) {
    this.objectCache = new Map();
    this.canvas = canvas;
    this.bounds = bounds;
    this.objects = objects;
  }

  calculateLineAngle(path) {
    if (path.length === 2) {
      const [x1, y1] = path[0].slice(1);
      const [x2, y2] = path[1].slice(1);
      return Math.atan2(y2 - y1, x2 - x1);
    } else {
      // For curved paths, calculate tangent at the end point
      const lastSegment = path[path.length - 1];
      const prevSegment = path[path.length - 2] || path[0];

      if (lastSegment[0] === 'Q') {
        // For quadratic curves, calculate tangent from control point to end point
        const controlX = lastSegment[1];
        const controlY = lastSegment[2];
        const endX = lastSegment[3];
        const endY = lastSegment[4];
        return Math.atan2(endY - controlY, endX - controlX);
      } else {
        // Fallback for other curve types
        const endX = lastSegment[lastSegment.length - 2];
        const endY = lastSegment[lastSegment.length - 1];
        const prevX = prevSegment[1] || prevSegment[prevSegment.length - 2];
        const prevY = prevSegment[2] || prevSegment[prevSegment.length - 1];
        return Math.atan2(endY - prevY, endX - prevX);
      }
    }
  }

  calculateStartAngle(path) {
    if (path.length === 2) {
      const [x1, y1] = path[0].slice(1);
      const [x2, y2] = path[1].slice(1);
      return Math.atan2(y2 - y1, x2 - x1);
    } else {
      // For curved paths, calculate tangent at the start point
      const firstSegment = path[0];
      const secondSegment = path[1];

      if (secondSegment[0] === 'Q') {
        // For quadratic curves, calculate tangent from start point to control point
        const startX = firstSegment[1];
        const startY = firstSegment[2];
        const controlX = secondSegment[1];
        const controlY = secondSegment[2];
        return Math.atan2(controlY - startY, controlX - startX);
      } else {
        // Fallback for other curve types
        const startX = firstSegment[1];
        const startY = firstSegment[2];
        const nextX = secondSegment[1] || secondSegment[secondSegment.length - 2];
        const nextY = secondSegment[2] || secondSegment[secondSegment.length - 1];
        return Math.atan2(nextY - startY, nextX - startX);
      }
    }
  }

  createPointerPath(pointer, stroke, strokeWidth, position, angle, opacity = 1) {
    const x = strokeWidth / 4;
    const [posX, posY] = position;

    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const transform = `matrix(${cos} ${sin} ${-sin} ${cos} ${posX} ${posY})`;

    switch (pointer) {
      case 'arrow': {
        const path = `M ${15 * x} 0 L ${0} ${15 * x} L ${0} ${-15 * x} Z`;
        return `<path d="${path}" fill="${stroke}" opacity="${opacity}" transform="${transform}"/>`;
      }
      case 'circle': {
        const radius = 10 * x;
        const centerX = 10 * x - 0.3;
        return `<circle cx="${centerX}" cy="0" r="${radius}" fill="${stroke}" opacity="${opacity}" transform="${transform}"/>`;
      }
      case 'square': {
        const size = 20 * x;
        return `<rect x="0" y="${-10 * x}" width="${size}" height="${size}" fill="${stroke}" opacity="${opacity}" transform="${transform}"/>`;
      }
      case 'line': {
        const length = 10 * x;
        return `<line x1="0" y1="${-length}" x2="0" y2="${length}" stroke="${stroke}" opacity="${opacity}" stroke-width="${strokeWidth}" transform="${transform}"/>`;
      }
      default:
        return '';
    }
  }

  extractObjects() {
    try {
      if (!this.objects || this.objects.length === 0) {
        return [];
      }

      const processedObjects = this.objects.map((obj) => {
        return {
          content: this.processObject(obj.object),
          zIndex: obj.zIndex,
        };
      });

      return processedObjects.filter(Boolean);
    } catch (error) {
      console.error('Error extracting objects:', error);
    }
  }

  processObject(fabricObject) {
    if (fabricObject.type === 'DynamicLine') {
      return this.processDynamicLine(fabricObject);
    }

    // Handle right-angle lines with rounded corners
    if (fabricObject.type === 'path' && fabricObject.rightAngle && fabricObject.isRounded) {
      return this.processRoundedRightAngleLine(fabricObject);
    }

    // Use Fabric.js built-in toSVG method for all other objects
    return this.getFabricSVG(fabricObject);
  }

  getFabricSVG(fabricObject) {
    try {
      // Store original values
      const originalLeft = fabricObject.left;
      const originalTop = fabricObject.top;

      // Temporarily adjust position for SVG generation
      fabricObject.left = originalLeft - this.bounds.actualLeft;
      fabricObject.top = originalTop - this.bounds.actualTop;

      // Generate SVG using Fabric.js built-in method
      let svgContent = fabricObject.toSVG();

      // Restore original position
      fabricObject.left = originalLeft;
      fabricObject.top = originalTop;

      return svgContent;
    } catch (error) {
      console.error('Error generating SVG for object:', error);
      return null;
    }
  }

  processDynamicLine(fabricObject) {
    const {
      path,
      stroke,
      strokeWidth,
      leftPointer,
      rightPointer,
      left,
      top,
      width,
      height,
      opacity = 1,
      flipX = false,
      flipY = false,
    } = fabricObject;

    // Calculate position offset
    const translatedX = left - this.bounds.actualLeft;
    const translatedY = top - this.bounds.actualTop;

    // For curved paths, we need to adjust coordinates by width/2 and height/2
    // to align with the object's coordinate system
    let pathData = '';
    if (path && path.length > 0) {
      pathData = path
        .map((segment) => {
          const cmd = segment[0];
          if (cmd === 'M' || cmd === 'L') {
            return `${cmd} ${segment[1] + width / 2} ${segment[2] + height / 2}`;
          } else if (cmd === 'Q') {
            return `${cmd} ${segment[1] + width / 2} ${segment[2] + height / 2} ${segment[3] + width / 2} ${segment[4] + height / 2}`;
          } else if (cmd === 'C') {
            return `${cmd} ${segment[1] + width / 2} ${segment[2] + height / 2} ${segment[3] + width / 2} ${segment[4] + height / 2} ${segment[5] + width / 2} ${segment[6] + height / 2}`;
          }
          return segment.join(' ');
        })
        .join(' ');
    }

    // Generate the path element
    let svgContent = `<path d="${pathData}" fill="transparent" stroke="${stroke}" stroke-width="${strokeWidth}" opacity="${opacity}"/>`;

    // Add pointers if needed
    if (rightPointer && rightPointer !== 'remove' && path && path.length >= 2) {
      let endPoint, endAngle;

      const lastSegment = path[path.length - 1];
      if (lastSegment[0] === 'Q') {
        // For quadratic curves, end point is the last two parameters
        endPoint = [lastSegment[3] + width / 2, lastSegment[4] + height / 2];
        // Calculate angle at the end of the curve
        const controlX = lastSegment[1] + width / 2;
        const controlY = lastSegment[2] + height / 2;
        endAngle = Math.atan2(endPoint[1] - controlY, endPoint[0] - controlX);
      } else {
        // For straight lines
        endPoint = [lastSegment[1] + width / 2, lastSegment[2] + height / 2];
        const prevSegment = path[path.length - 2];
        endAngle = Math.atan2(
          endPoint[1] - (prevSegment[2] + height / 2),
          endPoint[0] - (prevSegment[1] + width / 2),
        );
      }

      svgContent += this.createPointerPath(
        rightPointer,
        stroke,
        strokeWidth,
        endPoint,
        endAngle,
        opacity,
      );
    }

    if (leftPointer && leftPointer !== 'remove' && path && path.length >= 2) {
      let startPoint, startAngle;

      const firstSegment = path[0];
      startPoint = [firstSegment[1] + width / 2, firstSegment[2] + height / 2];

      // Calculate start angle
      if (path[1][0] === 'Q') {
        // For quadratic curves
        const controlX = path[1][1] + width / 2;
        const controlY = path[1][2] + height / 2;
        startAngle = Math.atan2(controlY - startPoint[1], controlX - startPoint[0]) + Math.PI;
      } else {
        // For straight lines
        const nextPoint = [path[1][1] + width / 2, path[1][2] + height / 2];
        startAngle =
          Math.atan2(nextPoint[1] - startPoint[1], nextPoint[0] - startPoint[0]) + Math.PI;
      }

      svgContent += this.createPointerPath(
        leftPointer,
        stroke,
        strokeWidth,
        startPoint,
        startAngle,
        opacity,
      );
    }

    // Apply flip transformations to the group if needed
    const flipScaleX = flipX ? -1 : 1;
    const flipScaleY = flipY ? -1 : 1;

    let groupTransform = `translate(${translatedX} ${translatedY})`;

    if (flipX || flipY) {
      const centerX = width / 2;
      const centerY = height / 2;
      groupTransform += ` translate(${centerX}, ${centerY}) scale(${flipScaleX}, ${flipScaleY}) translate(${-centerX}, ${-centerY})`;
    }

    return `<g transform="${groupTransform}">${svgContent}</g>`;
  }

  processRoundedRightAngleLine(fabricObject) {
    const {
      path,
      stroke,
      strokeWidth,
      leftPointer,
      rightPointer,
      left,
      top,
      width,
      height,
      opacity = 1,
      flipX = false,
      flipY = false,
    } = fabricObject;

    // Calculate position offset
    const translatedX = left - this.bounds.actualLeft;
    const translatedY = top - this.bounds.actualTop;

    // Extract path data and apply rounded corners
    let pathData = '';

    if (path && path.length > 0) {
      // Convert path commands to SVG path data with rounded corners
      pathData = path
        .map((cmd, index) => {
          if (index === 0) {
            return `M ${cmd[1] + width / 2} ${cmd[2] + height / 2}`;
          }

          if (cmd[0] === 'L' && index < path.length - 1) {
            // For right-angle turns, use quadratic curves for rounding
            const prevCmd = path[index - 1];
            const nextCmd = path[index + 1];

            if (nextCmd) {
              const cornerRadius = Math.min(10, strokeWidth * 2); // Adjust radius based on stroke width
              const x1 = cmd[1] + width / 2;
              const y1 = cmd[2] + height / 2;
              const x2 = nextCmd[1] + width / 2;
              const y2 = nextCmd[2] + height / 2;

              // Calculate control point for rounded corner
              const dx = x2 - x1;
              const dy = y2 - y1;
              const len = Math.sqrt(dx * dx + dy * dy);

              if (len > cornerRadius * 2) {
                const offsetX = (dx / len) * cornerRadius;
                const offsetY = (dy / len) * cornerRadius;

                return `L ${x1 - offsetX} ${y1 - offsetY} Q ${x1} ${y1} ${x1 + offsetX} ${y1 + offsetY}`;
              }
            }
          }

          return `L ${cmd[1] + width / 2} ${cmd[2] + height / 2}`;
        })
        .join(' ');
    }

    let svgContent = `<path d="${pathData}" fill="transparent" stroke="${stroke}" stroke-width="${strokeWidth}" opacity="${opacity}" stroke-linecap="round" stroke-linejoin="round"/>`;

    // Add right pointer (end of line)
    if (rightPointer && rightPointer !== 'remove' && path && path.length >= 2) {
      const lastSegment = path[path.length - 1];
      const prevSegment = path[path.length - 2];
      const endPoint = [lastSegment[1] + width / 2, lastSegment[2] + height / 2];

      // Calculate angle using the same approach as in processDynamicLine
      const angle = Math.atan2(lastSegment[2] - prevSegment[2], lastSegment[1] - prevSegment[1]);

      svgContent += this.createPointerPath(
        rightPointer,
        stroke,
        strokeWidth,
        endPoint,
        angle,
        opacity,
      );
    }

    // Add left pointer (start of line) if specified
    if (leftPointer && leftPointer !== 'remove' && path && path.length >= 2) {
      const firstSegment = path[0];
      const secondSegment = path[1];
      const startPoint = [firstSegment[1] + width / 2, firstSegment[2] + height / 2];

      // Calculate angle from first to second point and add PI to reverse direction
      const startAngle =
        Math.atan2(secondSegment[2] - firstSegment[2], secondSegment[1] - firstSegment[1]) +
        Math.PI;

      svgContent += this.createPointerPath(
        leftPointer,
        stroke,
        strokeWidth,
        startPoint,
        startAngle,
        opacity,
      );
    }

    // Apply flip transformations to the group
    const flipScaleX = flipX ? -1 : 1;
    const flipScaleY = flipY ? -1 : 1;

    let groupTransform = `translate(${translatedX} ${translatedY})`;

    if (flipX || flipY) {
      const centerX = width / 2;
      const centerY = height / 2;
      groupTransform += ` translate(${centerX}, ${centerY}) scale(${flipScaleX}, ${flipScaleY}) translate(${-centerX}, ${-centerY})`;
    }

    return `<g transform="${groupTransform}">${svgContent}</g>`;
  }
}
